import { ROUTES } from "../components/shared/constant";
import { isModuleNeutralPath, isWorkforceContext } from "./workforcePaths";

export const EMPLOYEE_BASE_PATH = "/employee";

/**
 * True when the pathname belongs to the skyEmployee module.
 * Segment-bounded: "/employee", "/employee/...", "/employee?..." match;
 * "/employees" does not.
 */
export function isEmployeePath(pathname: string): boolean {
  return (
    pathname === EMPLOYEE_BASE_PATH ||
    pathname.startsWith(EMPLOYEE_BASE_PATH + "/") ||
    pathname.startsWith(EMPLOYEE_BASE_PATH + "?")
  );
}

/**
 * Is the shell currently showing skyEmployee? Its own paths, or a module-neutral
 * page (Users & access, the account page) opened from it — carried in
 * `?from=employee` the way `isWorkforceContext` reads `?from=workforce`.
 */
export function isEmployeeContext(pathname: string, search?: string): boolean {
  if (isEmployeePath(pathname)) return true;
  if (!isModuleNeutralPath(pathname)) return false;
  return new URLSearchParams(search ?? "").get("from") === "employee";
}

/**
 * The Employee shell, only while Employee is on for this person: arriving at a
 * shared page with `from=employee` after it was switched off falls back to the
 * Service Desk shell instead of a sidebar with nothing to show.
 */
export function employeeShellFor(
  pathname: string,
  search: string | undefined,
  enabled: boolean,
): boolean {
  return enabled && isEmployeeContext(pathname, search);
}

/** Prefix of skyEmployee's employee-facing view (Portal View → User Portal). */
export const EMPLOYEE_USER_VIEW_PATH = EMPLOYEE_BASE_PATH + "/my";

/**
 * True when the pathname is skyEmployee's employee-facing view. The view lives
 * in the URL, not in the shared portalView state, so switching to it never
 * touches the Service Desk customer portal.
 */
export function isEmployeeUserViewPath(pathname: string): boolean {
  return (
    pathname === EMPLOYEE_USER_VIEW_PATH ||
    pathname.startsWith(EMPLOYEE_USER_VIEW_PATH + "/") ||
    pathname.startsWith(EMPLOYEE_USER_VIEW_PATH + "?")
  );
}

/**
 * Where the header's "View profile" goes. Inside skyEmployee it is the
 * skyEmployee profile, in whichever view (admin or employee) the user is in;
 * everywhere else it is the account page, as before.
 */
export function viewProfileRoute(pathname: string): string {
  if (!isEmployeePath(pathname)) return ROUTES.ACCOUNT;
  return isEmployeeUserViewPath(pathname) ? ROUTES.EMPLOYEE_MY_PROFILE : ROUTES.EMPLOYEE_PROFILE;
}

/**
 * The module the shell is showing, as the `from` marker a module-neutral page
 * carries to keep it; null for Service Desk. Reads the context (path plus an
 * existing marker), not the path alone: on a shared page that is already
 * showing Workforce or Employee — Users & access, skyAgent, the account page
 * itself — the path says nothing, and reading it alone dropped the marker and
 * flipped the shell to Service Desk (#1046).
 */
export function moduleMarker(pathname: string, search?: string): "workforce" | "employee" | null {
  if (isWorkforceContext(pathname, search)) return "workforce";
  if (isEmployeeContext(pathname, search)) return "employee";
  return null;
}

/**
 * The header's "View profile" destination, query string included. The account
 * page is module-neutral, so it carries the current module's marker; the
 * skyEmployee profile is inside its module and needs none.
 */
export function viewProfileHref(pathname: string, search: string, queryString: string): string {
  const target = viewProfileRoute(pathname);
  const from = target === ROUTES.ACCOUNT ? moduleMarker(pathname, search) : null;
  if (!from) return target + queryString;
  const params = new URLSearchParams(queryString.replace(/^\?/, ""));
  params.set("from", from);
  return `${target}?${params.toString()}`;
}

/** Notification settings, in whichever skyEmployee view (admin or employee) the user is in. */
export function notificationSettingsRoute(pathname: string): string {
  return isEmployeeUserViewPath(pathname)
    ? ROUTES.EMPLOYEE_MY_NOTIFICATION_SETTINGS
    : ROUTES.EMPLOYEE_NOTIFICATION_SETTINGS;
}

// ── One page, two views ────────────────────────────────────────────────────
//
// The pages people use (Home, a post, My requests, Messages, Directory,
// Rewards, Resources, Benefits, answering a survey, the profile) exist in both views:
// at /employee/my/... in the employee view, and at the same address without
// "/my" in the admin view, where Admins and Managers see them with their own
// controls (and never have to switch views to see posts or act on them).
// Links are built as employee-view addresses and turned into the current
// view's with toViewPath, so a link never moves someone to the other view.

/** The pages that exist in both views, by their employee-view address. */
const SHARED_PAGES = [
  "/home",
  "/requests",
  "/new-request",
  "/messages",
  "/directory",
  "/rewards",
  "/leaderboard",
  "/surveys",
  "/resources",
  "/benefits",
  "/profile",
  "/notification-settings",
];

function isSharedPage(rest: string): boolean {
  return SHARED_PAGES.some(
    (p) => rest === p || rest.startsWith(p + "/") || rest.startsWith(p + "?"),
  );
}

/**
 * An employee-view address (/employee/my/...) as the same page in the view
 * `pathname` is in: unchanged in the employee view (or outside skyEmployee),
 * without "/my" in the admin view. Anything else is returned unchanged.
 */
export function toViewPath(path: string, pathname: string): string {
  if (!isEmployeePath(pathname) || isEmployeeUserViewPath(pathname)) return path;
  if (!isEmployeeUserViewPath(path)) return path;
  const rest = path.slice(EMPLOYEE_USER_VIEW_PATH.length);
  return isSharedPage(rest) ? EMPLOYEE_BASE_PATH + rest : path;
}

/**
 * An admin-view address of a page that also exists in the employee view, as
 * its employee-view address; null for an admin-only page. Someone without the
 * admin view who opens one (a shared link, say) lands on the same page.
 */
export function toUserViewPath(pathname: string): string | null {
  if (!isEmployeePath(pathname) || isEmployeeUserViewPath(pathname)) return null;
  const rest = pathname.slice(EMPLOYEE_BASE_PATH.length);
  return isSharedPage(rest) ? EMPLOYEE_USER_VIEW_PATH + rest : null;
}

/**
 * The skyEmployee address a page counts as for its view: its own, or — for a
 * module-neutral page shown in skyEmployee (Users & access, opened from the
 * admin menu) — the admin view's, so links from it stay in the admin view.
 */
export function employeeViewOf(pathname: string): string {
  return isEmployeePath(pathname) ? pathname : EMPLOYEE_BASE_PATH;
}

/** An employee-view address of a shared page as its admin-view address. */
export function adminViewPath(path: string): string {
  return toViewPath(path, EMPLOYEE_BASE_PATH);
}
